'use client';

import { useEffect, useRef, useState } from 'react';
import { currencyFormat } from '../utils/numbers';

interface IRollerProps {
  targetValue: number;
  id?: string;
  className?: string;
}

function usePreviousValue(value: number): number | undefined {
  const previousValue = useRef<number | undefined>(undefined);
  useEffect(() => {
    previousValue.current = value;
  });
  return previousValue.current;
}

export default function Roller({ targetValue, id, className }: IRollerProps) {
  const interval = useRef<number | null>(null);
  const frames = useRef<number[]>([]);
  const [displayValue, setDisplayValue] = useState('');
  const previousValue = usePreviousValue(targetValue);

  useEffect(() => {
    if (previousValue !== targetValue) {
      const nextFrames: number[] = [];
      const frameCount = Math.ceil(750 / (1000 / 30));
      const difference = (previousValue || 0) - targetValue;
      for (let frame = 0; frame <= frameCount; frame += 1) {
        const progress = frame / frameCount;
        const easing =
          progress < 0.5
            ? 2 * progress * progress
            : -1 + 2 * (2 - progress) * progress;
        nextFrames.push(targetValue + difference * easing);
      }
      if (interval.current) {
        window.clearInterval(interval.current);
      }
      frames.current = nextFrames;
    }

    interval.current = window.setInterval(renderFrame, 1000 / 30);
    function renderFrame(): void {
      if (frames.current.length === 0) {
        if (interval.current) {
          window.clearInterval(interval.current);
        }
        return;
      }
      const nextValue = frames.current.pop();
      setDisplayValue(nextValue ? currencyFormat(nextValue.toString()) : '');
    }

    return () => {
      if (interval.current) {
        window.clearInterval(interval.current);
      }
    };
  }, [previousValue, targetValue]);

  return (
    <span id={id} className={className || ''}>
      {displayValue || '$0'}
    </span>
  );
}
